import React, { useEffect, useRef } from "react";

import { isSessionExpired } from "helpers";

const defaultHandler = (_e: BeforeUnloadEvent) => {};

type UseBeforeUnloadProps = {
	onBeforeUnload: typeof defaultHandler;
	preventReloading: boolean;
};

export const useBeforeUnload = ({
	onBeforeUnload: handler = defaultHandler,
	preventReloading,
}: UseBeforeUnloadProps) => {
	if (
		process.env.NODE_ENV !== "production" &&
		typeof handler !== "function"
	) {
		throw new TypeError(
			`Expected "handler" to be a function, not ${typeof handler}.`,
		);
	}

	const handlerRef = useRef(handler);

	useEffect(() => {
		handlerRef.current = handler;
	}, [handler]);

	useEffect(() => {
		if (!preventReloading || isSessionExpired()) {
			return;
		}

		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			let returnValue;

			if (typeof handlerRef.current === "function") {
				returnValue = handlerRef.current(e);
			}

			if (e.defaultPrevented) {
				e.returnValue = "";
			}

			if (typeof returnValue === "string") {
				e.returnValue = returnValue;
				return returnValue;
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [preventReloading]);
};

type BeforeUnloadProps = {
	children?: JSX.Element | JSX.Element[];
	onBeforeUnload: typeof defaultHandler;
	preventReloading?: boolean;
};

export const BeforeUnload = ({
	children = undefined,
	onBeforeUnload,
	preventReloading = true,
}: BeforeUnloadProps) => {
	useBeforeUnload({ onBeforeUnload, preventReloading });

	return <>{children}</>;
};
